import React, { useEffect, useMemo, useRef, useState } from 'react';
import Drawer from '@material-ui/core/Drawer';
import { Box, Typography, Grid, FormControl, InputLabel, Select, MenuItem, Button, TextField, Divider, Chip, Link, List, ListItem, ListItemText, Tabs, Tab } from '@material-ui/core';
import MarkdownViewer from '../../Education/MarkdownViewer';
import AdminCommentsSection from './AdminCommentsSection';
import gql from 'graphql-tag';
import client from '../../../lib/apolloClient';

// GraphQL mutations
const CREATE_DOCUMENT = gql`
  mutation CreateDocument($file: File!, $name: String, $category: String, $document_type: Int) {
    createDocument(file: $file, name: $name, category: $category, document_type: $document_type) {
      id
      name
      download_url
      created_at
    }
  }
`;

const UPLOAD_PROOF = gql`
  mutation UploadProof($actionPlanItemId: ID!, $documentId: ID!) {
    upload_proof(action_plan_item_id: $actionPlanItemId, document_id: $documentId) {
      errors
    }
  }
`;

// A slideout drawer version of TaskDetailModal for admin sprint items
// Props:
// - open: boolean
// - onClose: () => void
// - task: minimal ActionPlanItem shape { id, title, description?, status, priority?, dueAt?, assigneeUserId?, domain?, cepaCategory?, taskTemplate?, documents? }
// - onSave: async (taskId, updates) => void
// - onMarkCompleted: async (taskId) => void
// - onDelete: async (taskId) => void
// - onTaskUpdated?: () => void  // Callback to refresh task data after upload
const SprintItemDrawer = ({ open, onClose, task, onSave, onMarkCompleted, onDelete, onTaskUpdated }) => {
  const [edited, setEdited] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [education, setEducation] = useState([]);
  const [eduPreview, setEduPreview] = useState(null);
  const [isEduLoading, setIsEduLoading] = useState(false);
  const titleRef = useRef(null);
  const [tab, setTab] = useState('info'); // info | education | comments

  useEffect(() => {
    if (open && task) {
      setEdited({
        title: task.title || '',
        description: task.description || '',
        status: (task.status || 'open').toLowerCase(),
        priority: (task.priority || 'medium').toLowerCase(),
        dueAt: task.dueAt || '',
        assigneeUserId: task.assigneeUserId || '',
      });
      setTimeout(() => titleRef.current && titleRef.current.focus(), 120);
      const keys = (task.taskTemplate?.key || task.taskTemplateKey || '').toString();
      if (keys) {
        const url = `/educational_contents.json?task_key=${encodeURIComponent(keys)}`;
        fetch(url, { credentials: 'same-origin' })
          .then(r => (r.ok ? r.json() : []))
          .then(list => {
            const arr = Array.isArray(list) ? list : (Array.isArray(list.educational_contents) ? list.educational_contents : []);
            setEducation(arr);
          })
          .catch(() => setEducation([]));
      } else {
        setEducation([]);
      }
    }
  }, [open, task]);

  const hasChanges = useMemo(() => {
    if (!task || !edited) return false;
    return (
      edited.title !== (task.title || '') ||
      edited.description !== (task.description || '') ||
      edited.status !== (task.status || 'open') ||
      edited.priority !== (task.priority || 'medium') ||
      (edited.dueAt || '') !== (task.dueAt || '') ||
      (edited.assigneeUserId || '') !== (task.assigneeUserId || '')
    );
  }, [task, edited]);

  if (!task) return null;

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      await onSave?.(task.id, edited);
    } catch (e) {
      setError('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleMarkCompleted = async () => {
    try {
      setIsSaving(true);
      setError(null);
      await onMarkCompleted?.(task.id);
      onClose?.();
    } catch (e) {
      setError('Failed to mark as completed');
    } finally {
      setIsSaving(false);
    }
  };

  const openEduPreview = async (ec) => {
    try {
      setIsEduLoading(true);
      const res = await fetch(`/educational_contents/${ec.id}.json`, { credentials: 'same-origin' });
      const data = await (res.ok ? res.json() : {});
      setEduPreview({
        id: ec.id,
        title: data.title || ec.title,
        contentType: (data.content_type || ec.content_type || '').toLowerCase(),
        mdBody: data.md_body || ec.md_body || '',
        assetUrl: ec.asset_url || ec.url || ''
      });
    } catch (_) {
      setEduPreview({ id: ec.id, title: ec.title, contentType: (ec.content_type || '').toLowerCase(), mdBody: ec.md_body || '', assetUrl: ec.asset_url || ec.url || '' });
    } finally {
      setIsEduLoading(false);
    }
  };

  const handleProofUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    
    // Capture the input element before async operations (React reuses synthetic events)
    const inputElement = e.target;
    
    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setError(`File size exceeds 10MB limit. File size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      
      // 1) Create Document via GraphQL mutation (using Shrine infrastructure)
      // Note: apollo-upload-client expects file as array for File! scalar type
      const docResult = await client.mutate({
        mutation: CREATE_DOCUMENT,
        variables: {
          file: [file],
          name: file.name,
          category: 'other',
          document_type: 0, // default_document
        },
        context: { hasUpload: true },
      });

      if (!docResult.data || !docResult.data.createDocument) {
        throw new Error('Failed to create document');
      }

      const documentId = docResult.data.createDocument.id;

      // 2) Link document to ActionPlanItem via GraphQL upload_proof
      const proofResult = await client.mutate({
        mutation: UPLOAD_PROOF,
        variables: {
          actionPlanItemId: task.id,
          documentId: documentId,
        },
      });

      if (proofResult.data?.upload_proof?.errors && proofResult.data.upload_proof.errors.length > 0) {
        throw new Error(proofResult.data.upload_proof.errors.join(', '));
      }

      // Reset file input using captured element
      if (inputElement) {
        inputElement.value = '';
      }

      // Notify parent to refresh task data
      if (onTaskUpdated) {
        onTaskUpdated();
      }

      setError(null);
    } catch (err) {
      console.error('Error uploading proof:', err);
      setError(err.message || 'Failed to upload proof');
    } finally {
      setIsSaving(false);
    }
  };

  const body = (
    <Box role="presentation" style={{ width: 720, maxWidth: '90vw' }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" p={2} borderBottom="1px solid var(--succession-border)">
        <Box>
          <Typography variant="caption" color="textSecondary">Task Details</Typography>
          <Typography variant="h6" style={{ lineHeight: 1.1 }}>{task.title}</Typography>
        </Box>
        <Button onClick={onClose}>Close</Button>
      </Box>

      <Box px={2} borderBottom="1px solid var(--succession-border)">
        <Tabs value={tab} onChange={(_,v)=>setTab(v)} indicatorColor="primary" textColor="primary">
          <Tab label="Info" value="info" />
          <Tab label="Education" value="education" />
          <Tab label="Comments" value="comments" />
        </Tabs>
      </Box>

      <Box display="flex" flexDirection="row">
        {/* Info tab - full width content */}
        {tab === 'info' && (
        <Box flex={1} p={2}>
          <TextField label="Title *" fullWidth inputRef={titleRef} value={edited?.title || ''} onChange={(e)=> setEdited({ ...edited, title: e.target.value })} variant="outlined" size="small" margin="dense" />
          <TextField label="Description" fullWidth multiline rows={4} value={edited?.description || ''} onChange={(e)=> setEdited({ ...edited, description: e.target.value })} variant="outlined" size="small" margin="dense" />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" size="small" fullWidth margin="dense">
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={edited?.status || 'open'} onChange={(e)=> setEdited({ ...edited, status: e.target.value })}>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="blocked">Blocked</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="wont_do">Won't Do</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" size="small" fullWidth margin="dense">
                <InputLabel>Priority</InputLabel>
                <Select label="Priority" value={edited?.priority || 'medium'} onChange={(e)=> setEdited({ ...edited, priority: e.target.value })}>
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Due Date" type="date" fullWidth variant="outlined" size="small" margin="dense" InputLabelProps={{ shrink: true }} value={edited?.dueAt ? new Date(edited.dueAt).toISOString().slice(0,10) : ''} onChange={(e)=> setEdited({ ...edited, dueAt: e.target.value ? new Date(e.target.value).toISOString() : null })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Assignee" placeholder="User ID or email" fullWidth variant="outlined" size="small" margin="dense" value={edited?.assigneeUserId || ''} onChange={(e)=> setEdited({ ...edited, assigneeUserId: e.target.value })} />
            </Grid>
          </Grid>

          <Box mt={2}>
            <Typography variant="caption" color="textSecondary">Supporting Documentation</Typography>
            <Box mt={0.5}>
              <label style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'6px 12px', border:'1px solid var(--succession-border)', borderRadius:4, cursor:'pointer' }}>
                Attach Proof
                <input type="file" style={{ display:'none' }} onChange={handleProofUpload} disabled={isSaving} />
              </label>
            </Box>
            {/* Display uploaded documents */}
            {task.documents && task.documents.length > 0 && (
              <Box mt={2} display="flex" flexDirection="column" gap={1}>
                {task.documents.map((doc) => (
                  <Box key={doc.id} display="flex" alignItems="center" justifyContent="space-between" p={1.5} bgcolor="action.hover" borderRadius={1} border="1px solid" borderColor="divider">
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2" noWrap>{doc.name || 'Unnamed document'}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {doc.created_at ? `Uploaded ${doc.created_at}` : ''}
                      </Typography>
                    </Box>
                    {doc.download_url && (
                      <Link href={doc.download_url} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 8, fontSize: '0.75rem' }}>
                        Download
                      </Link>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {error && <Typography color="error" variant="caption" style={{ display:'block', marginTop:8 }}>{error}</Typography>}

          <Box mt={2} display="flex" justifyContent="flex-end">
            {onDelete && (
              <Button onClick={()=> onDelete(task.id)} disabled={isSaving} style={{ marginRight: 8 }}>Delete</Button>
            )}
            <Button variant="contained" color="primary" onClick={handleSave} disabled={!hasChanges || isSaving} style={{ marginRight: 8 }}>{isSaving ? 'Saving…' : 'Save'}</Button>
            <Button onClick={handleMarkCompleted} disabled={isSaving}>Mark Completed</Button>
          </Box>

          {/* Info panel moved here (full width within Info tab) */}
          <Divider style={{ margin: '16px 0' }} />
          <Typography variant="subtitle2">Info</Typography>
          <Box mt={1}>
            <Typography variant="caption" color="textSecondary">ID</Typography>
            <Typography style={{ fontFamily:'monospace' }}>{task.id}</Typography>
          </Box>
          <Box mt={1}>
            <Typography variant="caption" color="textSecondary">Domain</Typography>
            <Box mt={0.5}><Chip size="small" label={(task.domain || task.taskTemplate?.domain || '—').toString().replace(/^./, (c)=>c.toUpperCase())} /></Box>
          </Box>
          <Box mt={1}>
            <Typography variant="caption" color="textSecondary">VMI Category</Typography>
            <Box mt={0.5}><Chip size="small" label={(task.cepaCategory || task.taskTemplate?.cepaCategory || '—').toString().replace(/^./, (c)=>c.toUpperCase())} /></Box>
          </Box>
        </Box>
        )}

        {/* Education tab - right column list */}
        {tab === 'education' && (
          <Box flex={1} p={2}>
            <>
              <Typography variant="subtitle2">Educational Support</Typography>
              {education && education.length > 0 ? (
                <List dense>
                  {education.map((ec) => (
                    <ListItem key={ec.id} button onClick={(e)=> { e.preventDefault(); openEduPreview(ec); }}>
                      <ListItemText
                        primary={
                          <Link href="#" onClick={(e)=> e.preventDefault()} style={{ fontSize: 16, fontWeight: 500 }}>
                            {ec.title}
                          </Link>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="textSecondary">No supporting education found.</Typography>
              )}
            </>
          </Box>
        )}

        {/* Comments tab - full width */}
        {tab === 'comments' && (
          <Box flex={1} p={2}>
            <Typography variant="subtitle2">Comments</Typography>
            <AdminCommentsSection commentableId={task.id} commentableType="ActionPlanItem" maxHeight={480} />
          </Box>
        )}
      </Box>

      {tab === 'education' && eduPreview && (
        <Box p={2} borderTop="1px solid var(--succession-border)">
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1">{eduPreview.title || 'Educational Content'}</Typography>
            <Box>
              {eduPreview?.contentType === 'markdown' && (
                <Button component="a" href={`/educational_contents/${eduPreview.id}.pdf`} target="_blank" rel="noopener noreferrer" variant="contained" color="primary">Download PDF</Button>
              )}
              <Button onClick={()=> setEduPreview(null)} style={{ marginLeft: 8 }}>Close</Button>
            </Box>
          </Box>
          {isEduLoading ? (
            <Typography variant="body2" color="textSecondary">Loading…</Typography>
          ) : (
            <Box>
              {eduPreview.contentType === 'markdown' && eduPreview.mdBody ? (
                <MarkdownViewer body={eduPreview.mdBody} />
              ) : eduPreview.contentType === 'iframe' && eduPreview.assetUrl ? (
                <Box style={{ width: '100%', height: 360 }}>
                  <iframe title="education-iframe" src={eduPreview.assetUrl} style={{ width: '100%', height: '100%', border: 0 }} />
                </Box>
              ) : eduPreview.assetUrl ? (
                <Typography variant="body2">Open: <a href={eduPreview.assetUrl} target="_blank" rel="noopener noreferrer">{eduPreview.assetUrl}</a></Typography>
              ) : (
                <Typography variant="body2" color="textSecondary">Content preview unavailable.</Typography>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <Drawer anchor="right" open={open} onClose={onClose} ModalProps={{ keepMounted: true }}>
      {body}
    </Drawer>
  );
};

export default SprintItemDrawer;


